import {
  eventById,
  formatMark,
  programmeName,
  type EventResultRow,
  type LeagueRow,
  type MeetResultRow,
  type Programme,
  type RecentMeet,
} from '@decathlon/shared';
import { clear, EVENT_EMOJI, h, ordinal, wrapTables } from '../dom.js';
import type { EventOutcome } from '../events/controller.js';

const hearts = (n: number) => '❤️'.repeat(n) + '🖤'.repeat(Math.max(0, 3 - n));
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

/** After each event: the event's places and marks, then the meet standings. */
export function renderEventResults(
  root: HTMLElement,
  m: {
    event: string;
    index: number;
    total: number;
    results: EventResultRow[];
    myId: string | null;
    nextAt: number;
    serverNow(): number;
    outcome: EventOutcome | null;
    isPb: boolean;
  },
  a: { leave(): void },
): void {
  clear(root);
  const ev = eventById(m.event);
  const me = m.results.find((r) => r.id === m.myId);
  const countdown = h('span', { class: 'muted small' });
  const tick = () => {
    const left = Math.max(0, Math.ceil((m.nextAt - m.serverNow()) / 1000));
    countdown.textContent =
      m.index + 1 < m.total ? `Next event in ${left} s` : `Final standings in ${left} s`;
    if (left > 0 && countdown.isConnected) setTimeout(tick, 250);
  };
  root.append(
    h(
      'div',
      { class: 'center' },
      h(
        'div',
        { class: 'panel results stack' },
        h(
          'div',
          { class: 'row', style: 'justify-content:space-between' },
          h('h1', {}, `${EVENT_EMOJI[ev.id] ?? '🏅'} ${ev.name}`),
          h('span', { class: 'badge' }, `Event ${m.index + 1} of ${m.total}`),
        ),
        me && me.participated !== false
          ? h(
              'div',
              { class: 'row big-result' },
              h(
                'div',
                { class: 'stat huge' },
                h('div', { class: 'v mono' }, formatMark(ev, me.mark)),
                h('div', { class: 'l' }, ev.unit === 'seconds' ? 'Your time' : 'Your best'),
              ),
              h(
                'div',
                { class: 'stat huge' },
                h('div', { class: 'v mono' }, String(me.points)),
                h('div', { class: 'l' }, 'Points'),
              ),
              h(
                'div',
                { class: 'stat huge' },
                h('div', { class: 'v' }, ordinal(me.place)),
                h('div', { class: 'l' }, 'In this event'),
              ),
              h(
                'div',
                { class: `stat huge ${me.passed ? 'pass' : 'fail'}` },
                h('div', { class: 'v' }, me.passed ? '✔' : '✘'),
                h(
                  'div',
                  { class: 'l' },
                  me.passed ? 'Qualified' : me.eliminated ? 'Eliminated' : 'Life lost',
                ),
              ),
            )
          : null,
        m.isPb ? h('div', { class: 'toast-inline ok' }, '🏆 New personal best!') : null,
        h(
          'table',
          {},
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', {}, '#'),
              h('th', {}, 'Athlete'),
              h('th', { class: 'num' }, 'Mark'),
              h('th', { class: 'num' }, 'Points'),
              h('th', { class: 'num' }, 'Total'),
              h('th', { class: 'num' }, 'Standing'),
              h('th', {}, 'Lives'),
            ),
          ),
          h(
            'tbody',
            {},
            m.results.map((r) =>
              h(
                'tr',
                { class: r.id === m.myId ? 'me' : '' },
                h('td', { class: 'place' }, r.place ? String(r.place) : '—'),
                h(
                  'td',
                  {},
                  h('span', { class: 'ab-dot', style: `background:${r.kit}` }),
                  ` ${r.name}`,
                  r.isBot
                    ? h('span', { class: 'badge bot', style: 'margin-left:6px' }, 'Bot')
                    : null,
                ),
                h(
                  'td',
                  { class: 'num' },
                  r.participated === false ? 'Spectating' : formatMark(ev, r.mark),
                ),
                h('td', { class: 'num' }, String(r.points)),
                h('td', { class: 'num' }, String(r.total)),
                h('td', { class: 'num' }, ordinal(r.standing)),
                h('td', {}, r.eliminated ? '☠️ out' : hearts(r.lives)),
              ),
            ),
          ),
        ),
        m.outcome?.tips.length
          ? h(
              'div',
              { class: 'stack' },
              m.outcome.tips
                .slice(0, 2)
                .map((t) => h('div', { class: 'hint' }, h('b', {}, '🧠 Coach'), t)),
            )
          : null,
        h(
          'div',
          { class: 'row', style: 'justify-content:space-between' },
          countdown,
          h('button', { class: 'btn small ghost', onclick: () => a.leave() }, '✕ Leave meet'),
        ),
      ),
    ),
  );
  tick();
  wrapTables(root);
}

/** End of the meet. */
export function renderPodium(
  root: HTMLElement,
  m: {
    results: MeetResultRow[];
    programme: Programme;
    myId: string | null;
    online: boolean;
    isHost: boolean;
  },
  a: { again(): void; league(): void; menu(): void; nextSeason?: (() => void) | undefined },
): void {
  clear(root);
  const top = m.results.filter((r) => r.place >= 1 && r.place <= 3);
  const me = m.results.find((r) => r.id === m.myId);
  const medals = ['🥇', '🥈', '🥉'];
  root.append(
    h(
      'div',
      { class: 'center' },
      h(
        'div',
        { class: 'panel results stack' },
        h('h1', {}, `🏅 ${programmeName(m.programme)} · final`),
        h(
          'div',
          { class: 'podium' },
          top.map((r) =>
            r
              ? h(
                  'div',
                  { class: `podium-step p${r.place}${r.id === m.myId ? ' me' : ''}` },
                  h('div', { class: 'medal' }, medals[r.place - 1]!),
                  h(
                    'div',
                    { class: 'podium-name' },
                    h('span', { class: 'ab-dot', style: `background:${r.kit}` }),
                    ` ${r.name}`,
                  ),
                  h('div', { class: 'podium-total mono' }, `${r.total} pts`),
                )
              : h('div', { class: 'podium-step empty' }),
          ),
        ),
        me
          ? h(
              'p',
              { class: 'lead', style: 'text-align:center' },
              `You finished ${ordinal(me.place)} with ${me.total} points${me.leaguePoints ? ` and earned ${me.leaguePoints} league points${me.bestEventBonus ? ' (including the best-event bonus)' : ''}` : ''}.`,
            )
          : null,
        h(
          'table',
          {},
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', {}, '#'),
              h('th', {}, 'Athlete'),
              h('th', { class: 'num' }, 'Total'),
              h('th', { class: 'num' }, 'Best event'),
              h('th', { class: 'num' }, 'League pts'),
            ),
          ),
          h(
            'tbody',
            {},
            m.results.map((r) =>
              h(
                'tr',
                { class: r.id === m.myId ? 'me' : '' },
                h('td', { class: 'place' }, String(r.place)),
                h(
                  'td',
                  {},
                  h('span', { class: 'ab-dot', style: `background:${r.kit}` }),
                  ` ${r.name}`,
                  r.eliminated ? ' ☠️' : '',
                ),
                h('td', { class: 'num' }, String(r.total)),
                h('td', { class: 'num' }, String(r.bestEvent)),
                h('td', { class: 'num' }, r.isBot ? '—' : String(r.leaguePoints)),
              ),
            ),
          ),
        ),
        h(
          'div',
          { class: 'row' },
          a.nextSeason
            ? h(
                'button',
                { class: 'btn primary', onclick: a.nextSeason },
                'Next season · harder standards',
              )
            : null,
          !m.online || m.isHost
            ? h(
                'button',
                { class: 'btn primary', onclick: () => a.again() },
                m.online ? '↻ Back to lobby' : '↻ Again',
              )
            : h('span', { class: 'muted' }, 'Waiting for the host to continue.'),
          h('button', { class: 'btn blue', onclick: () => a.league() }, '🏆 League'),
          h('button', { class: 'btn ghost', onclick: () => a.menu() }, '← Menu'),
        ),
      ),
    ),
  );
  wrapTables(root);
}

export function renderLeague(
  root: HTMLElement,
  rows: LeagueRow[],
  recent: RecentMeet[],
  myName: string,
  onBack: () => void,
): void {
  clear(root);
  root.append(
    h(
      'div',
      { class: 'center' },
      h(
        'div',
        { class: 'panel wide stack' },
        h(
          'div',
          { class: 'row', style: 'justify-content:space-between' },
          h('h1', {}, '🏆 League'),
          h('button', { class: 'btn small ghost', onclick: onBack }, '← Back'),
        ),
        h(
          'p',
          { class: 'muted small' },
          'Completed full decathlons with at least two human athletes award 25, 18, 15, 12, 10, 8 points, plus 5 for the best event. Single-event practice and solo meets are unranked.',
        ),
        rows.length
          ? h(
              'table',
              {},
              h(
                'thead',
                {},
                h(
                  'tr',
                  {},
                  h('th', {}, '#'),
                  h('th', {}, 'Athlete'),
                  h('th', { class: 'num' }, 'Meets'),
                  h('th', { class: 'num' }, 'Wins'),
                  h('th', { class: 'num' }, 'Podiums'),
                  h('th', { class: 'num' }, 'Best total'),
                  h('th', { class: 'num' }, 'Points'),
                ),
              ),
              h(
                'tbody',
                {},
                rows.map((r, i) =>
                  h(
                    'tr',
                    { class: r.name.toLowerCase() === myName.toLowerCase() ? 'me' : '' },
                    h('td', { class: 'place' }, String(i + 1)),
                    h('td', {}, r.name),
                    h('td', { class: 'num' }, String(r.meets)),
                    h('td', { class: 'num' }, String(r.wins)),
                    h('td', { class: 'num' }, String(r.podiums)),
                    h('td', { class: 'num' }, String(r.bestTotal)),
                    h('td', { class: 'num' }, String(r.points)),
                  ),
                ),
              ),
            )
          : h('p', { class: 'muted' }, 'No meets yet. Finish one to start the table.'),
        recent.length ? h('h2', {}, 'Recent meets') : null,
        recent.length
          ? h(
              'table',
              {},
              h(
                'tbody',
                {},
                recent.map((r) =>
                  h(
                    'tr',
                    {},
                    h('td', {}, fmtDate(r.playedAt)),
                    h('td', {}, r.programme),
                    h('td', {}, `Winner: ${r.winner}`),
                    h(
                      'td',
                      { class: 'num' },
                      `${r.athletes} athlete${r.athletes === 1 ? '' : 's'}`,
                    ),
                    h('td', { class: 'num' }, `${r.bestTotal} pts`),
                  ),
                ),
              ),
            )
          : null,
      ),
    ),
  );
  wrapTables(root);
}
